import {STORE_LOCATOR_CONFIGS} from '../components/store-locator/configs'

/**
* Returns the current day of the week as a lowercase string.
* @returns {string} The current day of the week (e.g., 'monday', 'tuesday').
*/
export const getCurrentDay = () => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const today = new Date()
    return days[today.getDay()]
}

/**
* Returns the name of the next day of the week given the current day.
* @param {string} currentDay - The current day of the week (e.g., 'monday', 'tuesday').
* @returns {string} The name of the next day of the week.
*/
export const getNextDay = (currentDay) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const currentIndex = days.indexOf(currentDay)
    const nextIndex = (currentIndex + 1) % 7
    return days[nextIndex]
}

/**
* Retrieves the opening and closing hours for a specific day from a store object.
* @param {Object} store - The store object containing daily open and close time properties (e.g., c_MondayOpen, c_MondayClose).
* @param {string} day - The day of the week (e.g., 'Monday', 'Tuesday') for which to retrieve hours.
* @returns {{open: string, close: string} | null} An object with 'open' and 'close' times if the store is open on the given day, or null if closed or times are unavailable.
*/
export const getStoreHoursForDay = (store, day) => {
    const openKey = `c_${day}Open`
    const closeKey = `c_${day}Close`

    const openTime = store[openKey]
    const closeTime = store[closeKey]

    if (openTime === 'CLOSED' || closeTime === 'CLOSED' || !openTime || !closeTime) {
        return null
    }

    return {
        open: openTime,
        close: closeTime
    }
}

/**
* Converts a time string in "HH:MM" format to the total number of minutes.
* @param {string} timeStr - The time string to convert, formatted as "HH:MM".
* @returns {number} The total number of minutes represented by the input time.
*/
export const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number)
    return hours * 60 + minutes
}

/**
* Returns the current time in minutes since midnight.
* @returns {number} The number of minutes elapsed since 00:00 today.
*/
export const getCurrentTimeInMinutes = () => {
    const now = new Date()
    return now.getHours() * 60 + now.getMinutes()
}

/**
* Formats a 24-hour time string (e.g., "14:30") into a 12-hour time format with am/pm.
* @param {string} timeStr - The time string in "HH:mm" 24-hour format.
* @returns {string|null} The formatted time string in "h:mm am/pm" format, or null if input is falsy.
*/
export const formatTime = (timeStr) => {
    if (!timeStr) {
        return null
    }

    const [hours, minutes] = timeStr.split(':').map(Number)
    const period = hours >= 12 ? 'pm' : 'am'
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`
}

/**
* Finds the next opening day and its hours for a given store, starting from a specified day.
* Iterates through the next 7 days (excluding the start day) to determine when the store is next open.
*
* @param {Object} store - The store object containing hours information.
* @param {string} startDay - The day of the week to start searching from (e.g., 'monday').
* @returns {{day: string, hours: Object}|null} An object with the next opening day and its hours, or null if not found within 7 days.
*/
export const getNextOpeningDay = (store, startDay) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const startIndex = days.indexOf(startDay)

    // Check next 7 days
    for (let i = 1; i <= 7; i++) {
        const dayIndex = (startIndex + i) % 7
        const day = days[dayIndex]
        const hours = getStoreHoursForDay(store, day)

        if (hours) {
            return { day, hours }
        }
    }

    return null
}

/**
* Formats a day name string by capitalizing its first letter.
* @param {string} day - The name of the day to format (e.g., "monday").
* @returns {string} The formatted day name with the first letter capitalized (e.g., "Monday").
*/
export const formatDayName = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1)
}

/**
* Determines the current and upcoming opening status of a store based on its hours and the current time.
*
* @param {Object} store - The store object containing hours of operation and related data.
* @returns {Object} An object representing the store's status, which may include:
*   - {string} status - One of 'OPEN_AT', 'OPEN_UNTIL', 'OPEN_TOMORROW', 'OPEN_NEXT_DAY', or 'NOT_AVAILABLE'.
*   - {string} [time] - The formatted opening or closing time for today, if applicable.
*   - {string} [openTime] - The formatted opening time for the next opening day.
*   - {string} [closeTime] - The formatted closing time for the next opening day.
*   - {string} [day] - The formatted name of the next opening day, if not tomorrow.
*
* The function uses helper utilities to determine the current day, current time in minutes, and store hours.
* It handles scenarios where the store is currently open, will open later today, is closed for the day,
* or is closed for multiple days, returning appropriate status and timing information.
*/
export const getStoreHoursData = (store) => {
    const currentDay = getCurrentDay()
    const currentMinutes = getCurrentTimeInMinutes()
    const todayHours = getStoreHoursForDay(store, currentDay)

    // If store is closed today
    if (!todayHours) {
        const nextOpening = getNextOpeningDay(store, currentDay)
        if (nextOpening) {
            if (nextOpening.day === getNextDay(currentDay)) {
                return {
                    status: 'OPEN_TOMORROW',
                    openTime: formatTime(nextOpening.hours.open),
                    closeTime: formatTime(nextOpening.hours.close)
                }
            } else {
                return {
                    status: 'OPEN_NEXT_DAY',
                    day: formatDayName(nextOpening.day),
                    openTime: formatTime(nextOpening.hours.open),
                    closeTime: formatTime(nextOpening.hours.close)
                }
            }
        }
        return { status: 'NOT_AVAILABLE' }
    }

    const openingMinutes = timeToMinutes(todayHours.open)
    const closingMinutes = timeToMinutes(todayHours.close)

    // If store opens later today (before opening time)
    if (currentMinutes < openingMinutes) {
        return {
            status: 'OPEN_AT',
            time: formatTime(todayHours.open)
        }
    }

    // If store is currently open
    if (currentMinutes >= openingMinutes && currentMinutes < closingMinutes) {
        return {
            status: 'OPEN_UNTIL',
            time: formatTime(todayHours.close)
        }
    }

    // If store is closed for the day (after closing time)
    const nextOpening = getNextOpeningDay(store, currentDay)
    if (nextOpening) {
        if (nextOpening.day === getNextDay(currentDay)) {
            return {
                status: 'OPEN_TOMORROW',
                openTime: formatTime(nextOpening.hours.open),
                closeTime: formatTime(nextOpening.hours.close)
            }
        } else {
            return {
                status: 'OPEN_NEXT_DAY',
                day: formatDayName(nextOpening.day),
                openTime: formatTime(nextOpening.hours.open),
                closeTime: formatTime(nextOpening.hours.close)
            }
        }
    }

    return { status: 'NOT_AVAILABLE' }
}

/**
* Groups consecutive days of the week that share the same opening and closing hours for a given store.
*
* @param {Object} store - The store object containing daily opening and closing hour properties (e.g., c_mondayOpen, c_mondayClose, etc.).
* @returns {Array<Object>} An array of grouped objects, each containing:
*   - {string} timeKey: A string representing the open-close time range (e.g., "09:00-18:00").
*   - {string} open: The opening time for the group.
*   - {string} close: The closing time for the group.
*   - {Array<Object>} days: An array of day objects included in the group, each with {name, short, open, close}.
*
* If the store object is not provided, returns an empty array.
*/
export const getGroupedStoreHours = (store) => {
    if (!store) return []

    const days = [
        { name: 'Monday', short: 'Mon', open: store.c_mondayOpen, close: store.c_mondayClose },
        { name: 'Tuesday', short: 'Tue', open: store.c_tuesdayOpen, close: store.c_tuesdayClose },
        { name: 'Wednesday', short: 'Wed', open: store.c_wednesdayOpen, close: store.c_wednesdayClose },
        { name: 'Thursday', short: 'Thu', open: store.c_thursdayOpen, close: store.c_thursdayClose },
        { name: 'Friday', short: 'Fri', open: store.c_fridayOpen, close: store.c_fridayClose },
        { name: 'Saturday', short: 'Sat', open: store.c_saturdayOpen, close: store.c_saturdayClose },
        { name: 'Sunday', short: 'Sun', open: store.c_sundayOpen, close: store.c_sundayClose }
    ]

    const groups = []
    let currentGroup = null

    days.forEach((day) => {
        const timeKey = `${day.open}-${day.close}`

        if (currentGroup && currentGroup.timeKey === timeKey) {
            currentGroup.days.push(day)
        } else {
            if (currentGroup) {
                groups.push(currentGroup)
            }
            currentGroup = {
                timeKey,
                open: day.open,
                close: day.close,
                days: [day]
            }
        }
    })

    if (currentGroup) {
        groups.push(currentGroup)
    }

    return groups
}

/**
* Formats grouped store hours into a human-readable array of strings.
*
* Each group represents a range of days with the same opening and closing times.
* If a group is marked as 'CLOSED', it will be displayed as closed for that day range.
* Otherwise, the opening and closing times are formatted and included in the output.
* If the store has no defined hours, an empty array is returned.
*
* @param {Object} store - The store object containing hours and scheduling information.
* @returns {string[]} An array of formatted strings representing the store's grouped hours.
*/
export const formatGroupedHours = (store) => {
    const groups = getGroupedStoreHours(store)
    let formattedGroups = groups.map(group => {
        const dayRange = group.days.length === 1
            ? group.days[0].short
            : `${group.days[0].short} - ${group.days[group.days.length - 1].short}`

        if (group.open === 'CLOSED' || group.close === 'CLOSED') {
            return `<b>${dayRange}</b> Closed`
        }

        const openTime = formatTime(group.open)
        const closeTime = formatTime(group.close)

        return `<b>${dayRange}</b> ${openTime} - ${closeTime}`
    })

    if (groups.length === 1 && groups[0].timeKey === 'undefined-undefined') {
        return []
    }

    return formattedGroups
}

/**
* Builds the Google Maps directions URL for a store, using its address so the destination matches
* the one displayed, and falling back to its coordinates.
* @param {Object} store - The store object.
* @returns {string} The directions URL.
*/
export const getStoreDirectionsURL = (store) => {
    const address = [
        store?.address1,
        store?.address2,
        store?.city,
        store?.stateCode || store?.state,
        store?.postalCode,
        store?.countryCode
    ]
        .filter(Boolean)
        .join(', ')

    const latitude = store?.latitude ?? store?.lat
    const longitude = store?.longitude ?? store?.lng
    const destination =
        address || (latitude != null && longitude != null ? `${latitude},${longitude}` : '')

    return STORE_LOCATOR_CONFIGS.getDirectionURL + encodeURIComponent(destination)
}
